import type { MutableRefObject } from 'react';
import * as turf from '@turf/turf';
import { toast } from 'react-toastify';
import type { Feature, MultiPolygon, Point, Polygon, Geometry } from 'geojson';
import { observability } from 'utils/observability';
import { zoomMapViewToGraphic } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/mapViewZoom';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from './buildGeoLocationFromGeoJson';

const extractFeaturesFromGeoJson = (parsed: unknown): Feature[] => {
  if (!parsed || typeof parsed !== 'object') return [];
  const obj = parsed as {
    type?: unknown;
    features?: unknown;
    geometry?: unknown;
    coordinates?: unknown;
  };
  if (obj.type === 'FeatureCollection' && Array.isArray(obj.features)) {
    return obj.features as Feature[];
  }
  if (obj.type === 'Feature' && obj.geometry) {
    return [obj as Feature];
  }
  if (typeof obj.type === 'string' && obj.coordinates !== undefined) {
    return [
      { type: 'Feature', geometry: obj as Feature['geometry'], properties: {} },
    ];
  }
  return [];
};

type ResolvedImport = {
  geometry: Geometry;
  centroidFeature: Feature;
  skipped: { index: number; type: string }[];
};

const isPointFeature = (feature: Feature): feature is Feature<Point> =>
  feature.geometry?.type === 'Point';

const isPolygonFeature = (feature: Feature): feature is Feature<Polygon> =>
  feature.geometry?.type === 'Polygon';

const isSupportedFeature = (
  feature: Feature
): feature is Feature<Point | Polygon> =>
  isPointFeature(feature) || isPolygonFeature(feature);

const resolveImportedFeatures = (
  allFeatures: Feature[]
): ResolvedImport | { error: string } => {
  const points: { index: number; feature: Feature<Point> }[] = [];
  const polygons: { index: number; feature: Feature<Polygon> }[] = [];
  allFeatures.forEach((feature, index) => {
    if (isPointFeature(feature)) {
      points.push({ index, feature });
    } else if (isPolygonFeature(feature)) {
      polygons.push({ index, feature });
    }
  });

  const isPurePolygonCollection =
    polygons.length >= 1 && polygons.length === allFeatures.length;

  if (isPurePolygonCollection && polygons.length > 1) {
    let union: Feature<Polygon | MultiPolygon> | null = polygons[0].feature;
    for (let i = 1; i < polygons.length; i++) {
      union = turf.union(
        union as Feature<Polygon | MultiPolygon>,
        polygons[i].feature
      );
      if (!union) break;
    }
    if (!union) {
      return { error: 'Could not combine the polygons in the imported file.' };
    }
    return {
      geometry: union.geometry,
      centroidFeature: turf.centroid(union),
      skipped: [],
    };
  }

  if (isPurePolygonCollection) {
    const only = polygons[0].feature;
    return {
      geometry: only.geometry,
      centroidFeature: turf.centroid(only),
      skipped: [],
    };
  }

  const chosen = allFeatures.find(isSupportedFeature);
  if (!chosen) {
    const types = allFeatures
      .map((f, i) => `#${i} (${f.geometry?.type ?? 'unknown'})`)
      .join(', ');
    return {
      error: `No supported geometries (Point or Polygon) found. Skipped: ${types}`,
    };
  }
  const geometry = chosen.geometry;
  const centroidFeature = turf.centroid(chosen);
  const firstSupportedIndex = allFeatures.findIndex(isSupportedFeature);
  const skipped = allFeatures
    .map((f, i) => ({ index: i, type: f.geometry?.type ?? 'unknown' }))
    .filter((s) => s.index !== firstSupportedIndex);

  return { geometry, centroidFeature, skipped };
};

type UseGeoJsonImportArgs = {
  setLocation?: (location: GeoLocation) => void;
  setWatchLocation?: (watch: boolean) => void;
  reverseGeocode: (
    longitude: number,
    latitude: number
  ) => Promise<{ address?: string; placeName?: string }>;
  drawLocationOnMap: (
    geometry?: Geometry,
    address?: string,
    targetMapView?: __esri.MapView | null
  ) => void;
  drawingLayerRef: MutableRefObject<__esri.GraphicsLayer>;
  mapView: __esri.MapView | null;
};

export const useGeoJsonImport = ({
  setLocation,
  setWatchLocation,
  reverseGeocode,
  drawLocationOnMap,
  drawingLayerRef,
  mapView,
}: UseGeoJsonImportArgs) => {
  const onImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !setLocation) return;

    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        toast.error('Imported file is not valid JSON.');
        return;
      }

      const allFeatures = extractFeaturesFromGeoJson(parsed);
      if (allFeatures.length === 0) {
        toast.error('No features found in the imported GeoJSON.');
        return;
      }

      const resolved = resolveImportedFeatures(allFeatures);
      if ('error' in resolved) {
        toast.error(resolved.error);
        return;
      }
      const { geometry, centroidFeature, skipped } = resolved;

      if (skipped.length > 0) {
        const skippedText = skipped
          .map((s) => `#${s.index} (${s.type})`)
          .join(', ');
        toast.warning(
          `Imported the first valid geometry. Skipped: ${skippedText}`
        );
      }

      const [centerLng, centerLat] =
        centroidFeature.geometry.type === 'Point'
          ? (centroidFeature.geometry.coordinates as [number, number])
          : [0, 0];

      const { address, placeName } = await reverseGeocode(centerLng, centerLat);

      const newGeoLocation = buildGeoLocationFromGeoJson({
        geometry,
        centroid: centroidFeature,
        address,
        placeName,
      });

      setWatchLocation?.(true);
      setLocation(newGeoLocation);
      drawLocationOnMap(geometry, placeName || address || undefined);

      const graphic = drawingLayerRef.current.graphics.getItemAt(0);
      if (graphic && mapView) {
        zoomMapViewToGraphic({ mapView, graphic });
      }
    } catch (error) {
      observability.captureException(error);
      toast.error('Failed to import GeoJSON file.');
    }
  };

  return { onImportFileChange };
};
